'use client';
import Image from 'next/image';
import { useState } from 'react';

import {
  useCreateSrmQuestionsMutation,
  useGetSrmQuestionsQuery,
} from '../../api';
import askQuestionsPng from '../../images/ask-questions.png';
import { Button, Modal } from '../../ui';

export const SupplierRequestMatchAskQuestionsModal = ({
  matchId,
  closeModal,
}: {
  matchId: string;
  closeModal: () => void;
}) => {
  const [askQuestions, { isLoading }] = useCreateSrmQuestionsMutation();
  const { refetch: refetchSrmQuestions, isLoading: isRefetching } =
    useGetSrmQuestionsQuery(matchId);
  const [questions, setQuestions] = useState<string[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleCloseModal = () => {
    setQuestions([]);
    closeModal();
  };

  const handleSubmitQuestions = async () => {
    await askQuestions({ matchId, questions });
    refetchSrmQuestions();
    setShowConfirmation(true);
  };

  return !showConfirmation ? (
    <Modal
      title={'Ask Questions'}
      icon={'/icons/rocket.svg'}
      showModal={true}
      handleCloseModal={handleCloseModal}
    >
      <div className="flex gap-8">
        <div className="flex flex-col gap-8 flex-1">
          <div className="flex flex-col gap-2">
            <span className="text-2xl font-bold text-text-primary">
              Questions for Supplier
            </span>
            <span className="text-base font-normal text-text-secondary">
              If you have any questions regarding this supplier opportunity,
              please enter them below. The enterprise will respond to your
              questions shortly.
            </span>
          </div>
          {questions.length > 0 && (
            <div className="flex flex-col gap-4 transition ease-in-out delay-150">
              {questions.map((question, index) => (
                <div key={index} className="my-2">
                  <textarea
                    className={`w-full rounded-2xl bg-gray-100 resize-none p-3 sm:min-h-[50px] md:min-h-[50px] lg:min-h-[75px] transition ease-in-out delay-150`}
                    placeholder="Enter your question here..."
                    onChange={(e) => {
                      questions[index] = e.target.value;
                      setQuestions([...questions]);
                    }}
                    value={question}
                  />
                  <button
                    className="text-error-700"
                    type="button"
                    disabled={isLoading || isRefetching}
                    onClick={() => {
                      questions.splice(index, 1);
                      setQuestions([...questions]);
                    }}
                  >
                    Remove Question
                  </button>
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            disabled={isLoading}
            className="flex align-center cursor-pointer gap-3"
            onClick={() => setQuestions([...questions, ''])}
          >
            <Image
              src="/icons/plus.svg"
              alt="Add Questions"
              width={24}
              height={24}
            />
            <span className=" transition text-primary-700">Add Question</span>
          </button>
          <div className="flex gap-3">
            <div className="w-fit">
              <Button
                text="Submit Questions"
                variant={
                  isLoading ||
                  isRefetching ||
                  questions.length < 1 ||
                  questions.some((question) => question == '')
                    ? 'disabled'
                    : 'primary'
                }
                onClick={handleSubmitQuestions}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-1 justify-center">
          <Image
            src={askQuestionsPng}
            alt="Supplier Request Image"
            priority
            height={570}
            style={{ objectFit: 'contain' }}
          />
        </div>
      </div>
    </Modal>
  ) : (
    <Modal
      title={'Questions Submitted'}
      icon={'/icons/sparkles.svg'}
      confirmation={true}
      showCloseButton={true}
      showModal={true}
      handleCloseModal={handleCloseModal}
    >
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-4 items-center">
          <span className="text-text-secondary max-w-[640px] text-center">
            Your questions have been successfully submitted to the enterprise.
            You will be notified once they respond.
          </span>
        </div>
        <Image
          src="/questions-confirmation.png"
          alt="Confirmation"
          width={1024}
          height={300}
          style={{ objectFit: 'contain' }}
        />
      </div>
    </Modal>
  );
};
